'use client'

import React, { useEffect, useRef } from 'react'
import { ShoppingCart, Bike } from 'lucide-react'
import type { NavStyle } from '@/lib/microsite/navStyle'

type Props = {
  businessName: string
  cartCount: number
  logoUrl?: string
  onOrder: () => void
  localeSwitcher?: React.ReactNode
  /** Barra sólida desde el top, siempre (spec 2026-10-19 §3). `dark` = #171717
   *  (default de toda tienda); `light` = blanca, para logos oscuros. Ya no hay
   *  modo transparente: sobre la foto del hero el logo blanco se perdía. */
  navStyle?: NavStyle
  /** Muestra el botón "Delivery" (el merchant tiene al menos un link activo).
   *  El popup se controla en el padre (RestaurantMicrosite) para compartir una
   *  sola instancia entre el header y el hero. */
  hasDelivery?: boolean
  onDelivery?: () => void
}

export function FloatingNav({ businessName, cartCount, logoUrl, onOrder, localeSwitcher, navStyle = 'dark', hasDelivery, onDelivery }: Props) {
  const dark = navStyle === 'dark'

  // Publica el borde inferior real del nav para que la barra "¿Cómo lo recibes?"
  // y la de categorías sepan dónde pegarse (sticky). No hay alto fijo: cambia con
  // el banner superior (--banner-height) y con el logo. Medir es más honesto que
  // adivinar (spec 2026-09-03 §4.8).
  const navRef = useRef<HTMLElement>(null)
  useEffect(() => {
    const el = navRef.current
    if (!el || typeof ResizeObserver === 'undefined') return
    const publish = () =>
      document.documentElement.style.setProperty('--ms-nav-h', `${Math.round(el.getBoundingClientRect().bottom)}px`)
    publish()
    const ro = new ResizeObserver(publish)
    ro.observe(el)
    return () => {
      ro.disconnect()
      document.documentElement.style.removeProperty('--ms-nav-h')
    }
  }, [dark])

  return (
    <nav
      ref={navRef}
      data-nav-style={navStyle}
      className={`fixed left-0 right-0 z-40 shadow-sm border-b ${dark ? 'border-white/10' : 'border-black/5'}`}
      style={{
        top: 'var(--banner-height, 0px)',
        background: dark ? '#171717' : '#ffffff',
      }}
    >
      <div className="max-w-6xl mx-auto px-4 sm:px-8 py-2.5 flex items-center justify-between">
        {logoUrl ? (
          // eslint-disable-next-line @next/next/no-img-element
          <img src={logoUrl} alt={businessName} className="h-7 w-auto object-contain" />
        ) : (
          <span
            className="font-bold text-base"
            style={{ color: dark ? '#fff' : 'var(--ms-text)' }}
          >
            {businessName}
          </span>
        )}

        <div className="flex items-center gap-2.5">
          {localeSwitcher && <div>{localeSwitcher}</div>}
          {hasDelivery && onDelivery && (
            <button
              type="button"
              onClick={onDelivery}
              className={`inline-flex items-center gap-2 rounded-full px-3.5 py-1.5 text-sm font-semibold border transition ${
                dark
                  ? 'border-white/30 text-white hover:bg-white/10'
                  : 'border-gray-300 text-gray-900 hover:bg-gray-100'
              }`}
            >
              <Bike className="w-4 h-4" />
              <span className="hidden sm:block">Delivery</span>
            </button>
          )}
          <button
            type="button"
            onClick={onOrder}
            className={`relative inline-flex items-center gap-2 rounded-full px-3.5 py-1.5 text-sm font-semibold transition ${
              dark
                ? 'bg-white text-[#171717] hover:bg-white/90'
                : 'bg-gray-900 text-white hover:bg-gray-800'
            }`}
          >
            <ShoppingCart className="w-4 h-4" />
            <span className="hidden sm:block">Ver carrito</span>
            {cartCount > 0 && (
              <span
                className="absolute -top-1 -right-1 min-w-[20px] h-5 px-1 rounded-full text-white text-xs font-bold flex items-center justify-center"
                style={{ background: 'var(--ms-accent)' }}
              >
                {cartCount}
              </span>
            )}
          </button>
        </div>
      </div>
    </nav>
  )
}
